// DOCS-ALWAYS-IN-MEMORY: the doc's memory chip beside its kind ("Indexed in Recall" / "Indexing" /
// "Not indexed"), from `pages.memory`. Short labels only; a company admin gets the per-space detail
// behind an (i). Nothing while the route is pending or for a task's body.
import { Badge, InfoTip } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { BrainIcon } from "lucide-react";
import type { Api } from "../../api/client";
import { formatDateTime } from "../format";
import { pageMemoryQuery } from "./work-queries";

// WIKI-IN-COMPANYOS: agents read every doc they may view in CompanyOS itself; the chip only says
// whether the optional Recall index holds a copy, so it never implies agents depend on it.
const LABEL = {
  in_memory: ["Indexed in Recall", "success"],
  syncing: ["Indexing", "info"],
  not_in_memory: ["Not indexed", "outline"],
} as const;

const WHY: Record<string, string> = {
  private: "Private doc: kept out of shared memory.",
  restricted: "Restricted doc: kept out of memory.",
  archived: "Archived.",
  opted_out: "Its spaces are switched off in Setup › Recall.",
  memory_off: "Memory isn't set up for this company.",
};

export function DocMemoryChip({ api, co, pageId }: { api: Api; co: string; pageId: string }) {
  const q = useQuery(pageMemoryQuery(api, co, pageId));
  const m = q.data;
  if (!m) return null;
  const [label, variant] = LABEL[m.state];
  return (
    <span className="inline-flex items-center gap-0.5" data-slot="page-memory">
      <Badge variant={variant} data-state={m.state}>
        <BrainIcon aria-hidden="true" />
        {label}
      </Badge>
      {m.spaces ? (
        <InfoTip label="About the Recall index">
          <p>Agents read this doc in CompanyOS whether or not it is indexed.</p>
          {m.reason ? <p>{WHY[m.reason]}</p> : null}
          {m.spaces.length ? (
            <ul className="space-y-1" data-slot="page-memory-spaces">
              {m.spaces.map((s) => (
                <li key={s.space_id}>
                  <span className="font-medium text-foreground">{s.name}</span>
                  {s.via === "shared" ? " (shared)" : ""}:{" "}
                  {!s.sync_enabled
                    ? "off"
                    : s.current
                      ? `in memory${s.synced_at ? ` · ${formatDateTime(s.synced_at)}` : ""}`
                      : "syncing"}
                  {s.last_error ? (
                    <span className="block text-destructive">{s.last_error}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </InfoTip>
      ) : null}
    </span>
  );
}
